import { Button, Stack } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { Meeting } from "features/Resources/Meeting/models";
import { Text14 } from "components/Text";
import { NoMeetingsBeachDay } from "components/EmptyComponents/RandomEmptyState";
import { NewHireJourneyId } from "models/newHire";
import { DisplayAttendeesColumn } from "features/Resources/Meeting/Components/DisplayAttendees";
import { useNewHireJourneyMeetingsQuery } from "features/Resources/Meeting/hooks";
import { PrefixId } from "features/Resources/Shared/models";
import { AdminMeetingTypeTag } from "design-system";
import moment from "moment";
import { formattedTimezone, getLocalTimezone } from "helpers/time";
import { OtherActionsColumn } from "./OtherActionsColumn";
import ScheduledAtColumn from "./ScheduledAtColumn";

const columnHelper = createColumnHelper<Meeting>();

const columns = (onExpand: (prefixId: PrefixId) => void) => [
  { accessorKey: "__section" },
  columnHelper.accessor("meetingType", {
    header: "",
    enableSorting: false,
    cell: ({ getValue }) => <AdminMeetingTypeTag.IconBadge meetingType={getValue()} />,
    maxSize: 50,
    minSize: 50,
    size: 50,
    meta: {
      sx: {
        paddingLeft: "24px !important",
        paddingRight: 0,
      },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "scheduled",
    header: `When (${formattedTimezone(getLocalTimezone(), "short")})`,
    enableSorting: false,
    cell: (row) => (
      <ScheduledAtColumn meeting={row.getValue()} />
    ),
  }),
  columnHelper.accessor((row) => row, {
    id: "name",
    enableSorting: false,
    header: "Name",
    maxSize: 270,
    cell: (row) => (
      <Button variant="unstyled" width="fit-content" onClick={() => onExpand(row.getValue().prefixId)}>
        <Text14
          wordBreak="break-word"
          textDecorationLine={row.getValue()?.status === "skipped" ? "line-through" : "none"}
        >
          {row.getValue()?.name}
        </Text14>
      </Button>
    ),
  }),
  columnHelper.accessor((row) => row, {
    id: "attendees",
    header: "Guests",
    enableSorting: false,
    cell: (row) => (
      <DisplayAttendeesColumn
        attendees={row.getValue().attendees}
        allSessionAttendees={row.getValue().allSessionAttendees}
      />
    ),
  }),
  columnHelper.accessor((row) => row, {
    id: "other_actions",
    enableSorting: false,
    header: "",
    cell: (row) => <OtherActionsColumn meeting={row.getValue()} />,
  }),
];

interface BaseMeetingSchedulingTableProps {
  onExpand: (prefixId: PrefixId) => void;
  data: Meeting[] | undefined;
  isLoading?: boolean;
}

function sectionForMeeting(meeting: Meeting) {
  switch (meeting.status) {
    case "pending_scheduling":
    case "failed":
    case "removed":
      return "Unscheduled";
    case "skipped":
      return "Skipped";
    case "scheduled":
    case "completed":
    case "cancelled":
      return moment(meeting.scheduledAt).format("ddd, MM/D");
    default:
      return "Unscheduled";
  }
}

export function BaseMeetingSchedulingTable({ data, onExpand, isLoading }: BaseMeetingSchedulingTableProps) {
  const sectionedData = (data ?? []).map((m) => ({ ...m, __section: sectionForMeeting(m) }));
  return (
    <Stack direction="column" gap="4" w="100%">
      <ControlledDataTable<Meeting>
        data={sectionedData}
        columns={columns(onExpand)}
        isLoading={isLoading}
        emptyStateComponent={<NoMeetingsBeachDay />}
        initialPagination={{ pageSize: 250, pageIndex: 0 }}
      />
    </Stack>
  );
}

export default function MeetingSchedulingTable({ newHireJourneyId, onExpand }: {
  newHireJourneyId: NewHireJourneyId,
  onExpand: (prefixId: PrefixId) => void;
}) {
  const { data, isPending: isLoading } = useNewHireJourneyMeetingsQuery(newHireJourneyId);

  return <BaseMeetingSchedulingTable data={data} isLoading={isLoading} onExpand={onExpand} />;
}
